
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Globe, TrendingUp } from "lucide-react";

interface CountryOffersChartProps {
  data: any[];
  isLoading?: boolean;
}

export const CountryOffersChart = ({ data, isLoading }: CountryOffersChartProps) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80 bg-gray-200 rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  // تحويل البيانات للرسم البياني
  const chartData = data.slice(0, 8).map((country, index) => ({
    name: country.name.length > 20 ? country.name.substring(0, 20) + '...' : country.name,
    fullName: country.name,
    totalOffers: country.totalOffers,
    revenue: country.revenue,
    statusBreakdown: country.statusBreakdown,
    color: `hsl(${index * 45}, 65%, 55%)`
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border">
          <p className="font-semibold text-gray-800">{data.fullName}</p>
          <p className="text-lg font-bold text-emerald-600">
            العروض: {data.totalOffers}
          </p>
          <p className="text-md font-semibold text-blue-600">
            الإيرادات: {data.revenue.toLocaleString()} ر.س
          </p>
          <div className="mt-2 space-y-1">
            <p className="text-sm font-medium text-gray-700">حالات العملاء:</p>
            {Object.entries(data.statusBreakdown).map(([status, count]: [string, any]) => (
              <div key={status} className="flex justify-between text-xs">
                <span>{status}:</span>
                <span className="font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-800">
          <Globe className="w-6 h-6 text-emerald-600" />
          تحليل العروض حسب الدول
        </CardTitle>
        <p className="text-gray-600 text-sm">
          توزيع العروض والإيرادات على الدول المختلفة
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                tick={{ fill: '#666' }}
              />
              <YAxis 
                fontSize={12}
                tick={{ fill: '#666' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalOffers" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* إحصائيات إضافية */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <TrendingUp className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
            <div className="text-sm text-gray-600">إجمالي الدول</div>
            <div className="font-bold text-emerald-600">{data.length}</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600">إجمالي العروض</div>
            <div className="font-bold text-blue-600">
              {data.reduce((sum, country) => sum + country.totalOffers, 0)}
            </div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-sm text-gray-600">إجمالي الإيرادات</div>
            <div className="font-bold text-purple-600">
              {data.reduce((sum, country) => sum + country.revenue, 0).toLocaleString()} ر.س
            </div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-sm text-gray-600">متوسط العروض</div>
            <div className="font-bold text-orange-600">
              {Math.round(data.reduce((sum, country) => sum + country.totalOffers, 0) / data.length) || 0}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
